import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useEditorStore } from "@/stores/editor/editor-store";
import { Component } from "@/types";

interface AccessibilityContextValue {
  announceAction: (message: string) => void;
  focusComponent: (componentId: string) => void;
  getComponentAnnouncement: (component: Component) => string;
  isKeyboardNavigation: boolean;
}

const AccessibilityContext = createContext<AccessibilityContextValue | null>(
  null,
);

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error(
      "useAccessibility must be used within AccessibilityProvider",
    );
  }
  return context;
};

interface AccessibilityProviderProps {
  children: React.ReactNode;
}

export const AccessibilityProvider: React.FC<AccessibilityProviderProps> = ({
  children,
}) => {
  const {
    components,
    selectedComponents,
    selectComponent,
    clearSelection,
    moveComponent,
    deleteComponent,
    duplicateComponent,
    undo,
    redo,
    canUndo,
    canRedo,
    snapToGrid,
    gridSize,
  } = useEditorStore();

  const [isKeyboardNavigation, setIsKeyboardNavigation] = React.useState(false);
  const announcementRef = useRef<HTMLDivElement>(null);
  const lastFocusedComponent = useRef<string | null>(null);

  // Live region for screen reader announcements
  const announceAction = useCallback((message: string) => {
    if (announcementRef.current) {
      announcementRef.current.textContent = message;
      // Clear after a delay to allow for new announcements
      setTimeout(() => {
        if (announcementRef.current) {
          announcementRef.current.textContent = "";
        }
      }, 1000);
    }
  }, []);

  // Generate descriptive announcement for component
  const getComponentAnnouncement = useCallback((component: Component) => {
    const position = component.position
      ? `at position ${component.position.x}, ${component.position.y}`
      : "in flex container";

    let size = "auto size";
    if (component.size) {
      const width = component.size.width ?? "auto";
      const height = component.size.height ?? "auto";
      size = `size ${width} by ${height}`;
    }

    const status = component.locked ? "locked" : "unlocked";

    return `${component.name} ${component.type} component, ${position}, ${size}, ${status}`;
  }, []);

  // Focus a specific component programmatically
  const focusComponent = useCallback(
    (componentId: string) => {
      const element = document.querySelector(
        `[data-component-id="${componentId}"]`,
      );
      if (element && element instanceof HTMLElement) {
        element.focus();
        lastFocusedComponent.current = componentId;

        const component = components.find((c) => c.id === componentId);
        if (component) {
          announceAction(`Focused on ${getComponentAnnouncement(component)}`);
        }
      }
    },
    [components, getComponentAnnouncement, announceAction],
  );

  // Keyboard navigation handlers
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Track if user is using keyboard for navigation
      if (
        [
          "Tab",
          "ArrowUp",
          "ArrowDown",
          "ArrowLeft",
          "ArrowRight",
          "Enter",
          " ",
        ].includes(e.key)
      ) {
        setIsKeyboardNavigation(true);
      }

      // Global keyboard shortcuts
      if (
        e.target === document.body ||
        (e.target as HTMLElement).classList.contains("canvas-container")
      ) {
        const moveDistance = snapToGrid ? gridSize : 10;
        const selectedComponent = selectedComponents[0];

        switch (e.key) {
          case "Escape":
            e.preventDefault();
            clearSelection();
            announceAction("Selection cleared");
            break;

          case "Delete":
          case "Backspace":
            if (selectedComponents.length > 0) {
              e.preventDefault();
              selectedComponents.forEach((id) => deleteComponent(id));
              announceAction(
                `Deleted ${selectedComponents.length} component${selectedComponents.length > 1 ? "s" : ""}`,
              );
            }
            break;

          case "d":
            if ((e.ctrlKey || e.metaKey) && selectedComponent) {
              e.preventDefault();
              duplicateComponent(selectedComponent);
              announceAction("Component duplicated");
            }
            break;

          case "z":
            if ((e.ctrlKey || e.metaKey) && !e.shiftKey && canUndo()) {
              e.preventDefault();
              undo();
              announceAction("Action undone");
            }
            break;

          case "y":
          case "Z":
            if (
              ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "Z") ||
              ((e.ctrlKey || e.metaKey) && e.key === "y")
            ) {
              if (canRedo()) {
                e.preventDefault();
                redo();
                announceAction("Action redone");
              }
            }
            break;

          // Arrow key navigation for moving components
          case "ArrowUp":
            if (selectedComponent && !e.shiftKey) {
              e.preventDefault();
              const component = components.find(
                (c) => c.id === selectedComponent,
              );
              if (component) {
                const newY = Math.max(0, component.position.y - moveDistance);
                moveComponent(selectedComponent, {
                  ...component.position,
                  y: newY,
                });
                announceAction(
                  `Moved component up to position ${component.position.x}, ${newY}`,
                );
              }
            }
            break;

          case "ArrowDown":
            if (selectedComponent && !e.shiftKey) {
              e.preventDefault();
              const component = components.find(
                (c) => c.id === selectedComponent,
              );
              if (component) {
                const newY = component.position.y + moveDistance;
                moveComponent(selectedComponent, {
                  ...component.position,
                  y: newY,
                });
                announceAction(
                  `Moved component down to position ${component.position.x}, ${newY}`,
                );
              }
            }
            break;

          case "ArrowLeft":
            if (selectedComponent && !e.shiftKey) {
              e.preventDefault();
              const component = components.find(
                (c) => c.id === selectedComponent,
              );
              if (component) {
                const newX = Math.max(0, component.position.x - moveDistance);
                moveComponent(selectedComponent, {
                  ...component.position,
                  x: newX,
                });
                announceAction(
                  `Moved component left to position ${newX}, ${component.position.y}`,
                );
              }
            }
            break;

          case "ArrowRight":
            if (selectedComponent && !e.shiftKey) {
              e.preventDefault();
              const component = components.find(
                (c) => c.id === selectedComponent,
              );
              if (component) {
                const newX = component.position.x + moveDistance;
                moveComponent(selectedComponent, {
                  ...component.position,
                  x: newX,
                });
                announceAction(
                  `Moved component right to position ${newX}, ${component.position.y}`,
                );
              }
            }
            break;

          // Tab navigation through components
          case "Tab":
            if (!e.shiftKey) {
              const visibleComponents = components.filter(
                (c) => !c.hidden && !c.isDeleted,
              );
              if (visibleComponents.length > 0) {
                e.preventDefault();
                const currentIndex = selectedComponent
                  ? visibleComponents.findIndex(
                      (c) => c.id === selectedComponent,
                    )
                  : -1;
                const nextIndex = (currentIndex + 1) % visibleComponents.length;
                const nextComponent = visibleComponents[nextIndex];
                selectComponent(nextComponent.id);
                focusComponent(nextComponent.id);
              }
            } else {
              // Shift+Tab for reverse navigation
              const visibleComponents = components.filter(
                (c) => !c.hidden && !c.isDeleted,
              );
              if (visibleComponents.length > 0) {
                e.preventDefault();
                const currentIndex = selectedComponent
                  ? visibleComponents.findIndex(
                      (c) => c.id === selectedComponent,
                    )
                  : 0;
                const prevIndex =
                  currentIndex === 0
                    ? visibleComponents.length - 1
                    : currentIndex - 1;
                const prevComponent = visibleComponents[prevIndex];
                selectComponent(prevComponent.id);
                focusComponent(prevComponent.id);
              }
            }
            break;
        }
      }
    },
    [
      selectedComponents,
      components,
      selectComponent,
      clearSelection,
      moveComponent,
      deleteComponent,
      duplicateComponent,
      undo,
      redo,
      canUndo,
      canRedo,
      snapToGrid,
      gridSize,
      announceAction,
      focusComponent,
    ],
  );

  // Mouse usage detection
  const handleMouseDown = useCallback(() => {
    setIsKeyboardNavigation(false);
  }, []);

  // Focus management for components
  useEffect(() => {
    if (selectedComponents.length === 1 && isKeyboardNavigation) {
      const componentId = selectedComponents[0];
      if (componentId !== lastFocusedComponent.current) {
        focusComponent(componentId);
      }
    }
  }, [selectedComponents, isKeyboardNavigation, focusComponent]);

  // Event listeners
  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleMouseDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [handleKeyDown, handleMouseDown]);

  const contextValue: AccessibilityContextValue = {
    announceAction,
    focusComponent,
    getComponentAnnouncement,
    isKeyboardNavigation,
  };

  return (
    <AccessibilityContext.Provider value={contextValue}>
      {children}

      {/* Live region for screen reader announcements */}
      <div
        ref={announcementRef}
        className="sr-only"
        aria-live="polite"
        aria-atomic="true"
        role="status"
      />

      {/* Instructions for screen reader users */}
      <div className="sr-only">
        <h2>Keyboard shortcuts for the editor:</h2>
        <ul>
          <li>Tab: Navigate between components</li>
          <li>Arrow keys: Move selected component</li>
          <li>Enter or Space: Select component</li>
          <li>Delete: Delete selected component</li>
          <li>Ctrl+D: Duplicate selected component</li>
          <li>Ctrl+Z: Undo last action</li>
          <li>Ctrl+Y or Ctrl+Shift+Z: Redo last action</li>
          <li>Escape: Clear selection</li>
        </ul>
      </div>
    </AccessibilityContext.Provider>
  );
};
